import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppInfoService } from 'core/app-info/app-info.service';
import { CanComponentDeactivate } from 'core/unsaved-changes/unsaved-changes-guard.class';
import { AppLocalization } from '../../shared/entities/app-localization';
import { EditLeadComponent } from './edit-lead/edit-lead.component';

@Component({
  selector: 'app-leads',
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.scss'],
  standalone: false
})
export class LeadsComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(EditLeadComponent) editLeadComponent: EditLeadComponent;

  pathMap = ['', 'lead', 'lead-delivery', 'delivery-location', 'leads-management'];
  newDeliveryLocation = 'new-delivery-location';
  leadPath = 'lead';
  deliveryLocationPath = 'delivery-location';
  editAction = 'edit';
  newAction = 'new';
  appStrings: AppLocalization;
  customerId: string;
  title: string;
  displayedModule;
  displayedAction;
  itemId;
  breadcrumbs: any[] = [];
  isMobile: boolean;

  constructor(
    private appInfoService: AppInfoService,
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;
    this.displayedModule = (
      this.route.snapshot.url[1] && this.pathMap.indexOf(this.route.snapshot.url[1].path) > -1
    ) ? this.pathMap.indexOf(this.route.snapshot.url[1].path) : this.pathMap[0];
    this.displayedAction = this.route.snapshot.url[2] ? this.route.snapshot.url[2] : null;
    this.itemId = this.route.snapshot.url[3] ? this.route.snapshot.url[3] : null;
    this.setTitle();
    this.breadcrumbs = this.getBreadcrumbs();
  }

  setTitle() {
    const action = this.displayedAction?.path;
    const pathSegment = this.route.snapshot.url[1]?.path;

    const isEditMode = action === this.editAction;
    const isLeadPath = pathSegment === this.leadPath && action !== this.newDeliveryLocation;
    const isDeliveryPath = pathSegment === this.deliveryLocationPath || action === this.newDeliveryLocation;

    if (this.displayedModule === '') { // Add New Lead or Delivery Location
      this.title = this.appStrings.lead_newLeadDelivery_title;
    } else if (action === this.newDeliveryLocation) { // New Delivery Location
      this.title = this.appStrings.lead_newDelivery_title;
    } else if (isDeliveryPath) { // Edit or New Delivery Location
      this.title = isEditMode
        ? this.appStrings.lead_editDelivery_title
        : this.appStrings.lead_newDelivery_title;
    } else if (isLeadPath) { // Edit or New Lead
      this.title = isEditMode
        ? this.appStrings.label_edit_lead
        : this.appStrings.lead_newLead_title;
    }
  }

  close() {
    this.router.navigate(['/accounts']);
  }

  confirmClose() {
    if (this.editLeadComponent?.isDashboardRedirect) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/accounts']);
    }
  }

  confirmCancel() {
    if (this.editLeadComponent?.isDashboardRedirect) {
      this.router.navigate(['/dashboard']);
    } else if (this.editLeadComponent?.customerId) {
      this.router.navigate(['/accounts', this.editLeadComponent.customerId]);
    } else {
      this.router.navigate(['/accounts']);
    }
  }

  confirmDeleteLead(event: Event) {
    if (this.editLeadComponent) {
      this.editLeadComponent.confirmDeleteLead(event);
    }
  }

  saveChanges() {
    if (this.editLeadComponent) {
      this.editLeadComponent.saveChanges();
    }
  }

  editDeliverySaveChanges() {
    if (this.editLeadComponent) {
      this.editLeadComponent.editDeliverySaveChanges();
    }
  }

  navigateToNewLead() {
    this.router.navigate(['/leads', 'lead', 'new']);
  }

  navigateToNewDeliveryLocation() {
    this.router.navigate(['/leads', 'lead', 'new-delivery-location']);
  }

  getBreadcrumbs(): any[] {
    const breadcrumbs: any[] = [];
    const action = this.displayedAction?.path;
    const pathSegment = this.route.snapshot.url[1]?.path;

    // Determine the mode based on URL structure
    const isEditMode = action === this.editAction;
    const isNewMode = action === this.newAction || action === this.newDeliveryLocation;
    const isLeadPath = pathSegment === this.leadPath && action !== this.newDeliveryLocation;
    const isDeliveryPath = pathSegment === this.deliveryLocationPath || action === this.newDeliveryLocation;

    // First breadcrumb: Account Details (for edit lead) or Accounts list
    if (isEditMode && isLeadPath) {
      breadcrumbs.push({
        label: this.appStrings.contacts_accountDetails,
        url: `/accounts-leads/${this.itemId.path}`,
        icon: this.isMobile ? 'arrow_back' : null
      });
    } else {
      breadcrumbs.push({
        label: this.appStrings.menu_Accounts,
        url: '/accounts',
        icon: this.isMobile ? 'arrow_back' : null
      });
    }

    // Second breadcrumb: Add New (only for new lead/delivery)
    if (isNewMode || this.displayedModule === '') {
      breadcrumbs.push({
        label: this.appStrings.button_addNew,
        url: '/leads'
      });
    }

    // Final breadcrumb: Lead or Delivery Location
    if (isDeliveryPath) {
      breadcrumbs.push({ label: this.appStrings.lead_deliveryLocation });
    } else if (isLeadPath) {
      breadcrumbs.push({ label: this.appStrings.lead_lead });
    }

    return breadcrumbs;
  }

  canDeactivate() {
    return !this.editLeadComponent.leadForm.dirty;
  }

}
